import { useCallback, useEffect, useRef, useState } from "react";
import type { ModuleResult, YearFile } from "@ftr/tax-core";
import { Card } from "../components/ui.tsx";
import { decryptBackup, download, encryptBackup, type BackupContents } from "../lib/backup.ts";
import { LicenseContext, readLicense, useLicense, useLicenseState, type StoredLicense } from "../lib/license.ts";
import { clearAll, listSnapshots, nowIso, putYearFile, saveSnapshot, useYearFile } from "../lib/yearStore.ts";
import { OrganizerProvider, useOrganizer, type StepId } from "./context.tsx";
import { FuturesStep } from "./FuturesStep.tsx";
import { PropStep } from "./PropStep.tsx";
import { ReportStep } from "./ReportStep.tsx";
import { ReviewStep, stepFor } from "./ReviewStep.tsx";
import { YouStep } from "./YouStep.tsx";

export const TAX_YEAR = 2026;

/** The five steps. The top bar links to each as /organizer#<id>; blurb is what its folder page says. */
export const REPORT_STEPS: { id: StepId; label: string; title: string; blurb: string }[] = [
  { id: "you", label: "You", title: "About you", blurb: "Filing status and any W-2 jobs." },
  { id: "futures", label: "Futures", title: "Your futures accounts", blurb: "Year-end 1099-B totals for each account." },
  { id: "prop", label: "Prop firms", title: "Your prop firms", blurb: "Payouts and costs for each firm." },
  { id: "review", label: "Review", title: "Review", blurb: "Check each figure before you file." },
  { id: "report", label: "Report", title: "Your report", blurb: "Form 6781 and Schedule C, line by line." },
];
const STEPS = REPORT_STEPS;

/** The step a link asked for with #<id>, if it names one. */
function stepFromHash(): StepId | undefined {
  return STEPS.find((s) => `#${s.id}` === window.location.hash)?.id;
}

export function Organizer() {
  const { yf, update, replace, state } = useYearFile(TAX_YEAR);
  const lic = useLicenseState(TAX_YEAR);
  const [step, setStep] = useState<StepId>(() => stepFromHash() ?? "you");
  const go = useCallback((s: StepId) => {
    setStep(s);
    window.scrollTo(0, 0);
  }, []);
  // A top-bar link to a step while the report is already open changes only the hash.
  useEffect(() => {
    const on = () => {
      const s = stepFromHash();
      if (s) setStep(s);
    };
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  // Back from a finished checkout: open the report they just bought.
  useEffect(() => {
    if (lic.justPurchased) go("report");
  }, [lic.justPurchased, go]);
  if (!yf) return <p className="pending">Opening your tax report…</p>;
  return (
    <LicenseContext.Provider value={lic}>
      <OrganizerProvider yf={yf} update={update} replace={replace} saveState={state} go={go}>
        <Shell step={step} />
      </OrganizerProvider>
    </LicenseContext.Provider>
  );
}

const RANK: Record<ModuleResult<unknown>["state"], number> = { not_applicable: 0, ready: 1, provisional: 2, blocked: 3 };

/** The step's state badge: the worst of the modules it feeds, or counts of what's waiting. */
function useStepMarks(): Record<StepId, "done" | "todo" | "look" | null> {
  const { analysis } = useOrganizer();
  const issuesOn = (s: StepId) => analysis.issues.filter((i) => i.severity !== "info" && stepFor(i) === s);
  const mark = (s: StepId) => {
    const list = issuesOn(s);
    if (list.some((i) => i.severity === "blocker")) return "todo" as const;
    if (list.length) return "look" as const;
    return "done" as const;
  };
  const worstProp = Object.values(analysis.modules.propBusiness).reduce((w, r) => Math.max(w, RANK[r.state]), 0);
  const fut = analysis.modules.section1256.state;
  return {
    you: mark("you"),
    futures: fut === "blocked" ? "todo" : fut === "provisional" ? "look" : "done",
    prop: worstProp === 3 ? "todo" : worstProp === 2 ? "look" : "done",
    review: analysis.issues.some((i) => i.severity === "blocker") ? "todo" : analysis.issues.some((i) => i.severity === "review") ? "look" : "done",
    report: null,
  };
}

const MARK_LABEL = { done: "Done", todo: "Needs info", look: "Needs a look" } as const;

function Shell({ step }: { step: StepId }) {
  const { go, saveState, analysis } = useOrganizer();
  const marks = useStepMarks();
  const i = STEPS.findIndex((s) => s.id === step);
  const open = analysis.issues.filter((x) => x.severity !== "info").length;
  return (
    <div className="organizer">
      <nav className="steps no-print" aria-label="Tax report steps">
        <ol>
          {STEPS.map((s, n) => {
            const m = marks[s.id];
            return (
              <li key={s.id}>
                <button type="button" className={"step" + (s.id === step ? " step--on" : "")} aria-current={s.id === step ? "step" : undefined} onClick={() => go(s.id)}>
                  <span className={"step-dot" + (m ? ` step-dot--${m}` : "")} aria-hidden="true">
                    {m === "done" && s.id !== "report" ? "✓" : n + 1}
                  </span>
                  <span className="step-label">
                    {s.label}
                    {s.id === "review" && open > 0 && <span className="count">{open}</span>}
                  </span>
                  {m && <span className="sr-only"> ({MARK_LABEL[m]})</span>}
                </button>
              </li>
            );
          })}
        </ol>
        <SaveNote state={saveState} />
      </nav>

      <PurchaseNotice />
      <h2 className="step-title">{STEPS[i]!.title}</h2>
      {step === "you" && <YouStep />}
      {step === "futures" && <FuturesStep />}
      {step === "prop" && <PropStep />}
      {step === "review" && <ReviewStep />}
      {step === "report" && <ReportStep />}

      <div className="step-nav no-print">
        {i > 0 ? (
          <button type="button" className="btn btn--plain" onClick={() => go(STEPS[i - 1]!.id)}>
            ← {STEPS[i - 1]!.label}
          </button>
        ) : (
          <span />
        )}
        {i < STEPS.length - 1 && (
          <button type="button" className="btn btn--primary" onClick={() => go(STEPS[i + 1]!.id)}>
            Next: {STEPS[i + 1]!.label} →
          </button>
        )}
      </div>

      <DataPanel />
    </div>
  );
}

function PurchaseNotice() {
  const { notice, dismissNotice } = useLicense();
  if (!notice) return null;
  return (
    <div className={"callout no-print" + (notice.ok ? "" : " callout--warn")} role="status">
      {notice.text}{" "}
      <button type="button" className="link-btn" onClick={dismissNotice}>
        Dismiss
      </button>
    </div>
  );
}

function SaveNote({ state }: { state: string }) {
  const text =
    state === "unavailable"
      ? "This browser isn't saving your work (a private window can do this). Download a backup before you close it."
      : state === "saving"
        ? "Saving on this device…"
        : state === "loading"
          ? "Opening…"
          : "Saved on this device only";
  return (
    <p className={"save-note" + (state === "unavailable" ? " warn-text" : "")} role="status">
      {text}
    </p>
  );
}

function looksLikeYearFile(v: unknown): v is YearFile {
  const y = v as YearFile;
  return Boolean(y) && y.schema === 1 && typeof y.taxYear === "number" && Array.isArray(y.people) && Array.isArray(y.sources) && Array.isArray(y.events);
}

/** Backup, restore and erase. Everything happens in this browser. */
function DataPanel() {
  const { yf, replace } = useOrganizer();
  const lic = useLicense();
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [restorePass, setRestorePass] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const backup = async () => {
    setMsg(null);
    if (pass !== pass2) return setMsg({ ok: false, text: "The passphrases don't match." });
    setBusy(true);
    try {
      const contents: BackupContents = {
        kind: "ftr.organizer-backup",
        format: 1,
        savedAt: nowIso(),
        yearFiles: [yf],
        snapshots: await listSnapshots(yf.taxYear).catch(() => []),
        licenses: [readLicense(yf.taxYear)].filter((l): l is StoredLicense => l !== null),
      };
      download(`futures-tax-report-backup-${yf.taxYear}.ftrbackup`, await encryptBackup(contents, pass));
      setPass("");
      setPass2("");
      setMsg({ ok: true, text: "Backup downloaded. Keep the passphrase somewhere safe: without it the file can't be opened, and we can't recover it." });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : "Couldn't make the backup." });
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setMsg(null);
    const file = fileRef.current?.files?.[0];
    if (!file) return setMsg({ ok: false, text: "Choose a backup file first." });
    setBusy(true);
    try {
      const contents = await decryptBackup(await file.text(), restorePass);
      const years = contents.yearFiles.filter(looksLikeYearFile);
      if (!years.length) throw new Error("That backup has no tax report data in it.");
      if (!confirm("Replace what's in your tax report now with this backup?")) return;
      for (const y of years) {
        if (y.taxYear === yf.taxYear) replace(y);
        else await putYearFile(y);
      }
      for (const s of contents.snapshots) await saveSnapshot(s);
      if (contents.licenses?.length) lic.restore(contents.licenses);
      setRestorePass("");
      if (fileRef.current) fileRef.current.value = "";
      setMsg({ ok: true, text: `Restored your backup from ${new Date(contents.savedAt).toLocaleString()}.` });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : "Couldn't restore that backup." });
    } finally {
      setBusy(false);
    }
  };

  const erase = async () => {
    if (!confirm("Erase everything your tax report saved in this browser, including saved versions? This can't be undone.")) return;
    await clearAll().catch(() => undefined);
    window.location.reload();
  };

  return (
    <details className="data-panel no-print">
      <summary>Your data: backup, restore or erase</summary>
      <Card>
        <p className="card-intro">
          Your records are stored only in this browser, on this device. Nothing is sent to us. To move to another device or keep a copy, download an
          encrypted backup. It's locked with a passphrase you choose, in your browser, before the file is made.
        </p>
        <div className="form-section">
          <p className="form-section-label">Download an encrypted backup</p>
          <PassInput label="Passphrase (10 characters or more)" value={pass} onChange={setPass} autoComplete="new-password" />
          <PassInput label="Type it again" value={pass2} onChange={setPass2} autoComplete="new-password" />
          <button type="button" className="btn btn--outline btn--sm" onClick={backup} disabled={busy || pass.length < 10}>
            Download backup
          </button>
        </div>
        <div className="form-section">
          <p className="form-section-label">Restore from a backup</p>
          <div className="field">
            <label htmlFor="restore-file">Backup file</label>
            <input id="restore-file" ref={fileRef} type="file" accept=".ftrbackup,application/json" />
          </div>
          <PassInput label="Its passphrase" value={restorePass} onChange={setRestorePass} autoComplete="current-password" />
          <button type="button" className="btn btn--outline btn--sm" onClick={restore} disabled={busy || !restorePass}>
            Restore
          </button>
        </div>
        {msg && <p className={msg.ok ? "good-text" : "field-msg"}>{msg.text}</p>}
        <div className="form-section">
          <button type="button" className="link-btn link-btn--danger" onClick={erase}>
            Erase everything on this device
          </button>
        </div>
      </Card>
    </details>
  );
}

function PassInput(props: { label: string; value: string; onChange: (v: string) => void; autoComplete: string }) {
  const id = `pp-${props.label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className="field">
      <label htmlFor={id}>{props.label}</label>
      <input id={id} type="password" className="text-input" value={props.value} autoComplete={props.autoComplete} onChange={(e) => props.onChange(e.target.value)} />
    </div>
  );
}
